import {
  Button,
  ButtonGroup,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetMazdaWarrantyCausalPartsQuery,
  useGetServiceOrderIntegrationsMazdaPreauthorizationQuery,
  usePostServiceOrderIntegrationsMazdaPreauthorizationSendMutation,
  usePutServiceOrderIntegrationsMazdaPreauthorizationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {TestIdProps, suffixTestId} from 'shared/web';

import {getMazdaCatalogOptions} from '../../../../../utils/getMazdaCatalogOptions';
import {getMazdaCodeDescriptionLabel} from '../../../../../utils/getMazdaCodeDescriptionLabel';
import {
  MAINTENANCE_DONE_RIGHT,
  MAINTENANCE_NOT_DONE_CORRECTLY,
  MazdaPreauthSendFormSchema,
  MazdaPreauthSendFormType,
} from '../types/MazdaPreauthSendFormType';
import {hasMazdaWarrantyInstallationDetails} from '../utils/hasMazdaWarrantyInstallationDetails';
import {mapMazdaPreauthFormToRequestBody} from '../utils/mapMazdaPreauthFormToRequestBody';

const SCOPE_OPTIONS = [
  {value: 'warranty', label: i18n.t('entity.orderIntegration.labels.scopeWarranty')},
  {value: 'goodwill', label: i18n.t('entity.orderIntegration.labels.scopeGoodwill')},
];

const WARRANTY_TYPE_OPTIONS = [
  {value: 'basic_warranty', label: i18n.t('entity.orderIntegration.labels.warrantyTypeBasic')},
  {
    value: 'predelivery_inspection',
    label: i18n.t('entity.orderIntegration.labels.warrantyTypePredeliveryInspection'),
  },
  {
    value: 'part_warranty',
    label: i18n.t('entity.orderIntegration.labels.warrantyTypePartWarranty'),
  },
  {
    value: 'repeated_repair',
    label: i18n.t('entity.orderIntegration.labels.warrantyTypeRepeatedRepair'),
  },
];

const MAINTENANCE_OPTIONS = [
  {
    value: MAINTENANCE_DONE_RIGHT,
    label: i18n.t('entity.orderIntegration.labels.maintenanceDoneRight'),
  },
  {
    value: MAINTENANCE_NOT_DONE_CORRECTLY,
    label: i18n.t('entity.orderIntegration.labels.maintenanceNotDoneCorrectly'),
  },
];

interface MazdaPreauthSendDialogProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  onClose: () => void;
}

export function MazdaPreauthSendDialog(props: MazdaPreauthSendDialogProps) {
  const {
    data: preauthorization,
    isLoading,
    isError,
  } = useGetServiceOrderIntegrationsMazdaPreauthorizationQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {refetchOnMountOrArgChange: true}
  );

  const {data: causalParts} = useGetMazdaWarrantyCausalPartsQuery();

  const [putMazdaPreauthorization, {isLoading: isSaving}] =
    usePutServiceOrderIntegrationsMazdaPreauthorizationMutation();
  const [sendMazdaPreauthorization, {isLoading: isSending}] =
    usePostServiceOrderIntegrationsMazdaPreauthorizationSendMutation();

  const isBusy = isSaving || isSending;

  const defaultValues = {
    scope: preauthorization?.scope ?? undefined,
    warrantyType: preauthorization?.warrantyType ?? undefined,
    mileage: preauthorization?.mileage ?? undefined,
    vehicleServicedAccordingToManufacturerSpecs: match(
      preauthorization?.vehicleServicedAccordingToManufacturerSpecs
    )
      .with(true, always(MAINTENANCE_DONE_RIGHT))
      .with(false, always(MAINTENANCE_NOT_DONE_CORRECTLY))
      .otherwise(always(undefined)),
    installationDate: preauthorization?.installationDate ?? undefined,
    installationMileage: preauthorization?.installationMileage ?? undefined,
    installationRepairNo: preauthorization?.installationRepairNo ?? undefined,
    isSpecialCausalPart: isNotNilOrEmpty(preauthorization?.specialCausalPart),
    causalPart: preauthorization?.causalPart ?? undefined,
    specialCausalPart: preauthorization?.specialCausalPart ?? undefined,
    message: preauthorization?.message ?? undefined,
  };

  const preauthorizationArgs = {
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  };

  // The send endpoint takes no body — it reads the stored draft, so the form has to be saved first.
  const handleSend: FormSubmitHandler<MazdaPreauthSendFormType> = async (data) =>
    await putMazdaPreauthorization({
      ...preauthorizationArgs,
      body: mapMazdaPreauthFormToRequestBody(data),
    })
      .unwrap()
      .then(() => sendMazdaPreauthorization(preauthorizationArgs).unwrap())
      .then(props.onClose)
      .catch(handleApiError);

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
      <Form<MazdaPreauthSendFormType>
        onSubmit={handleSend}
        defaultValues={defaultValues}
        experimentalZodSchema={MazdaPreauthSendFormSchema}
      >
        {(control, formApi) => {
          const isSpecialCausalPart = formApi.watch('isSpecialCausalPart');
          const hasInstallationDetails = hasMazdaWarrantyInstallationDetails(
            formApi.watch('warrantyType')
          );

          // Draft semantics — Save and close persists whatever the user typed, without validation.
          const handleSaveAndClose = () =>
            putMazdaPreauthorization({
              ...preauthorizationArgs,
              body: mapMazdaPreauthFormToRequestBody(formApi.getValues()),
            })
              .unwrap()
              .then(props.onClose)
              .catch(handleApiError);

          return (
            <VStack spacing={4}>
              <Grid spacing={4} columns={2}>
                <FormField
                  control={control}
                  name="scope"
                  type="choice"
                  label={i18n.t('entity.orderIntegration.labels.scope')}
                  options={SCOPE_OPTIONS}
                  isNotClearable
                  isRequired
                  data-testid={suffixTestId('scope', props)}
                />
                <FormField
                  control={control}
                  name="warrantyType"
                  type="choice"
                  label={i18n.t('entity.orderIntegration.labels.warrantyType')}
                  options={WARRANTY_TYPE_OPTIONS}
                  isNotClearable
                  isRequired
                  data-testid={suffixTestId('warrantyType', props)}
                />
                <FormField
                  control={control}
                  name="mileage"
                  type="number"
                  label={i18n.t('entity.integration.labels.mileage')}
                  suffix={i18n.t('general.metric.km')}
                  isRequired
                  data-testid={suffixTestId('mileage', props)}
                />
                <Show when={hasInstallationDetails}>
                  <FormField
                    control={control}
                    name="installationDate"
                    type="apiDate"
                    label={i18n.t('entity.serviceOrder.labels.installationDate')}
                    isRequired
                    data-testid={suffixTestId('installationDate', props)}
                  />
                  <FormField
                    control={control}
                    name="installationMileage"
                    type="number"
                    label={i18n.t('entity.serviceOrder.labels.installationMileage')}
                    suffix={i18n.t('general.metric.km')}
                    isRequired
                    data-testid={suffixTestId('installationMileage', props)}
                  />
                  <FormField
                    control={control}
                    name="installationRepairNo"
                    type="text"
                    label={i18n.t('entity.orderIntegration.labels.installationRepairNo')}
                    isRequired
                    data-testid={suffixTestId('installationRepairNo', props)}
                  />
                </Show>
              </Grid>
              <FormField
                control={control}
                name="vehicleServicedAccordingToManufacturerSpecs"
                type="radio"
                label={i18n.t('entity.orderIntegration.labels.vehicleServicedAccordingToSpecs')}
                options={MAINTENANCE_OPTIONS}
                isRequired
                data-testid={suffixTestId('vehicleServicedAccordingToSpecs', props)}
              />
              <Separator spacing={0} />
              <FormField
                control={control}
                name="causalPart"
                type="textarea"
                minRows={1}
                label={i18n.t('entity.orderIntegration.labels.causalPart')}
                isRequired={!isSpecialCausalPart}
                isDisabled={isSpecialCausalPart}
                data-testid={suffixTestId('causalPart', props)}
              />
              <FormField
                control={control}
                name="isSpecialCausalPart"
                type="switch"
                label={i18n.t('entity.orderIntegration.labels.specialCausalPart')}
                onChange={(value) => {
                  if (!value) {
                    formApi.setValue('specialCausalPart', null);
                  }
                  formApi.setValue('causalPart', null);
                }}
                data-testid={suffixTestId('isSpecialCausalPart', props)}
              />
              <FormField
                control={control}
                name="specialCausalPart"
                type="choice"
                label={i18n.t('entity.orderIntegration.labels.specialCausalPart')}
                options={getMazdaCatalogOptions(
                  causalParts?.causalParts,
                  getMazdaCodeDescriptionLabel
                )}
                isRequired={isSpecialCausalPart}
                isDisabled={!isSpecialCausalPart}
                data-testid={suffixTestId('specialCausalPart', props)}
              />
              <FormField
                control={control}
                name="message"
                type="textarea"
                minRows={3}
                label={i18n.t('general.labels.message')}
                isRequired
                data-testid={suffixTestId('message', props)}
              />
              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    title={i18n.t('general.actions.discard')}
                    variant="secondary"
                    onClick={props.onClose}
                    isDisabled={isBusy}
                    data-testid={suffixTestId('discard', props)}
                  />
                  <Button
                    title={i18n.t('general.actions.saveAndClose')}
                    variant="secondary"
                    onClick={handleSaveAndClose}
                    isLoading={isSaving}
                    isDisabled={isBusy}
                    data-testid={suffixTestId('saveAndClose', props)}
                  />
                  <FormButton
                    control={control}
                    title={i18n.t('entity.orderIntegration.actions.send')}
                    type="submit"
                    isLoading={isBusy}
                    isDisabled={isBusy}
                    data-testid={suffixTestId('send', props)}
                  />
                </ButtonGroup>
              </DialogFooter>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
